import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Clock,
  Shield,
  Flame,
  Calendar,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PupilBooking() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [activeDay, setActiveDay] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Surface-1 Truth Hardening (Feb 2026): track how many of the booked
  // slots returned an authoritative `confirmed_at`. The success screen
  // only claims "confirmed and locked in" when every booked slot has
  // its authority field populated.
  const [confirmedSlotsCount, setConfirmedSlotsCount] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const { data: linkData } = await axios.get(`${API_URL}/api/bookings/link/${code}`);
      setInfo(linkData);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + weekOffset * 7);
      const { data: slots } = await axios.get(
        `${API_URL}/api/bookings/slots/${linkData.instructor_id}?start_date=${startDate.toISOString().split("T")[0]}&weeks=2`
      );
      setCalendar(slots);
      // Auto-select first available day
      if (!activeDay) {
        const firstAvail = slots.days.find((d) => !d.blocked && d.slots?.some((s) => s.status === "available"));
        if (firstAvail) setActiveDay(firstAvail.date);
      }
    } catch {
      setInfo(null);
    }
    setLoading(false);
  }, [code, weekOffset, activeDay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSlot = (date, time) => {
    const key = `${date}|${time}`;
    setSelectedSlots((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  };

  const bookAll = async () => {
    if (selectedSlots.length === 0) {
      toast.error("Select at least one slot");
      return;
    }
    setSubmitting(true);
    let booked = 0;
    let confirmedCount = 0; // Surface-1 Truth Hardening (Feb 2026)
    for (const slot of selectedSlots) {
      const [date, time] = slot.split("|");
      try {
        const { data } = await axios.post(`${API_URL}/api/bookings/instant`, {
          instructor_id: info.instructor_id,
          date,
          time,
          pupil_name: user?.name || "Pupil",
          pupil_email: user?.email || "",
          code,
        });
        booked++;
        // Only count a slot as confirmed when the backend returns
        // the authoritative `confirmed_at` timestamp.
        if (data?.confirmed_at) confirmedCount++;
      } catch (err) {
        if (err.response?.status === 409) {
          toast.error(`${time} on ${date} was just taken`);
        }
      }
    }
    setSubmitting(false);
    if (booked > 0) {
      setDone(true);
      // Pass the authoritative confirmation count through so the
      // success screen renders truth-bound wording.
      setConfirmedSlotsCount(confirmedCount);
      toast.success(`${booked} lesson${booked > 1 ? "s" : ""} booked!`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-dvh no-overflow-x bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-lg font-bold text-slate-900 mb-2">Link not found</p>
          <p className="text-sm text-slate-500">This booking link may have expired.</p>
        </div>
      </div>
    );
  }

  if (done) {
    // Surface-1 Truth Hardening (Feb 2026): the wording "confirmed and
    // locked in" requires an authoritative `confirmed_at` from EVERY
    // booked slot. Otherwise fall back to canonical pre-confirmation
    // language ("Awaiting confirmation").
    const allConfirmed =
      confirmedSlotsCount > 0 && confirmedSlotsCount === selectedSlots.length;
    return (
      <div className="min-h-dvh no-overflow-x bg-white flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-white" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Lessons Booked</h1>
          <p
            data-testid="pupil-booking-success-line"
            className="text-sm text-slate-500 mb-2"
          >
            {allConfirmed
              ? "Your slots are confirmed and locked in."
              : "Your slots are booked — awaiting confirmation."}
          </p>
          <p className="text-xs text-slate-400 mb-8">No payment until 3 days before each lesson.</p>
          {user ? (
            <Button onClick={() => navigate("/dashboard")} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl">
              Go to Dashboard
            </Button>
          ) : (
            <Button onClick={() => navigate("/register")} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl">
              Create Your Account
            </Button>
          )}
        </div>
      </div>
    );
  }

  const activeDayData = calendar?.days?.find((d) => d.date === activeDay);
  const summary = calendar?.summary;

  return (
    <div className="min-h-dvh no-overflow-x bg-white">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-1">
            {info.instructor_name}
          </p>
          <h1 className="text-xl font-bold text-slate-900 leading-tight">
            Secure your lesson slots
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Tap available slots to reserve them instantly.
          </p>
        </div>

        {/* Demand Pressure Banner */}
        {summary && (summary.demand_level === "high" || summary.demand_level === "medium") && (
          <div data-testid="demand-banner" className={`rounded-xl px-4 py-3 mb-5 flex items-center gap-2.5 ${
            summary.demand_level === "high" ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"
          }`}>
            <Flame className={`w-4 h-4 flex-shrink-0 ${summary.demand_level === "high" ? "text-red-500" : "text-amber-500"}`} />
            <p className={`text-sm font-medium ${summary.demand_level === "high" ? "text-red-700" : "text-amber-700"}`}>
              {summary.demand_level === "high"
                ? `Only ${summary.total_available} slots left — booking fast`
                : `${summary.total_booked} lessons already booked — slots filling up`}
            </p>
          </div>
        )}

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            data-testid="prev-week"
            onClick={() => { if (weekOffset > 0) { setWeekOffset(weekOffset - 1); setActiveDay(null); } }}
            disabled={weekOffset === 0}
            className={`p-2 rounded-lg ${weekOffset === 0 ? "text-slate-200" : "text-slate-500 hover:bg-slate-100"}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <p className="text-sm font-medium text-slate-700">
            {calendar?.days?.[0]?.date && new Date(calendar.days[0].date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            {" — "}
            {calendar?.days?.[calendar.days.length - 1]?.date && new Date(calendar.days[calendar.days.length - 1].date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </p>
          <button
            data-testid="next-week"
            onClick={() => { setWeekOffset(weekOffset + 1); setActiveDay(null); }}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day Strip */}
        <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 scrollbar-hide">
          {calendar?.days?.map((day) => {
            const d = new Date(day.date + "T00:00:00");
            const isActive = activeDay === day.date;
            const hasAvail = !day.blocked && day.slots?.some((s) => s.status === "available");
            const fillingFast = day.filling_fast;

            return (
              <button
                key={day.date}
                data-testid={`day-${day.date}`}
                onClick={() => !day.blocked && setActiveDay(day.date)}
                disabled={day.blocked}
                className={`flex-shrink-0 w-14 py-2.5 rounded-xl text-center transition-all ${
                  day.blocked
                    ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                    : isActive
                    ? "bg-slate-900 text-white"
                    : hasAvail
                    ? "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                    : "bg-red-50 border border-red-200 text-red-400"
                }`}
              >
                <p className="text-[10px] font-medium">{day.day_name}</p>
                <p className="text-sm font-bold">{d.getDate()}</p>
                {fillingFast && !isActive && !day.blocked && (
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mx-auto mt-0.5" />
                )}
                {day.blocked && <p className="text-[8px] mt-0.5">Off</p>}
              </button>
            );
          })}
        </div>

        {/* Time Slots */}
        {activeDay && activeDayData && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-slate-900">
                {new Date(activeDay + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
              </p>
              {activeDayData.filling_fast && (
                <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Flame className="w-3 h-3" /> Filling fast
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {activeDayData.slots.map((slot) => {
                const key = `${activeDay}|${slot.time}`;
                const isSelected = selectedSlots.includes(key);

                if (slot.status === "past") return null;

                return (
                  <button
                    key={slot.time}
                    data-testid={`slot-${activeDay}-${slot.time}`}
                    onClick={() => slot.status === "available" && toggleSlot(activeDay, slot.time)}
                    disabled={slot.status !== "available"}
                    className={`py-3 rounded-xl text-sm font-medium transition-all ${
                      slot.status === "booked"
                        ? "bg-red-50 text-red-300 border border-red-100 cursor-not-allowed"
                        : isSelected
                        ? "bg-slate-900 text-white ring-2 ring-slate-900 ring-offset-2"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    }`}
                  >
                    <Clock className="w-3.5 h-3.5 mx-auto mb-0.5" />
                    {slot.time}
                    {slot.status === "booked" && (
                      <p className="text-[9px] text-red-300 mt-0.5">Taken</p>
                    )}
                  </button>
                );
              })}
            </div>

            {activeDayData.slots.filter(s => s.status !== "past").length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">No slots on this day</p>
            )}
          </div>
        )}

        {/* Trust Signal */}
        <div className="bg-slate-50 rounded-xl px-4 py-3 mb-5 flex items-start gap-2.5">
          <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">No payment now. You only pay 3 days before each lesson.</p>
        </div>

        {/* Sticky CTA */}
        {selectedSlots.length > 0 && (
          <div className="sticky bottom-4">
            <Button
              data-testid="book-selected-btn"
              onClick={bookAll}
              disabled={submitting}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-base shadow-lg shadow-slate-900/20"
            >
              {submitting
                ? "Booking..."
                : `Book ${selectedSlots.length} Lesson${selectedSlots.length > 1 ? "s" : ""} Now`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
